import { Link } from 'react-router-dom';
import { Play } from 'lucide-react';
import { cn } from '@hrms/ui';
import {
  formatTrainingDuration,
  resolveTrainingText,
  type TrainingLanguage,
  type TrainingLearnerHome,
} from '@hrms/shared';
import { CourseCover } from '../ui/index.js';
import { dueInWords, learnerCoursePath } from '../myLearning.js';
import { localizeText } from '../player/lessonLanguage.js';
import { useLessonLanguage } from '../player/useLessonLanguage.js';
import { LearningProgressBar } from './LearningProgressBar.js';

type ContinueLearning = NonNullable<TrainingLearnerHome['continueLearning']>;

/** "Next: How to raise a complaint · 7 min" — the lesson named in `language` when it has that title. */
export function nextLessonLine(
  next: ContinueLearning['nextLesson'],
  language: TrainingLanguage | null = null,
): string | null {
  if (!next) return null;
  const { title } = resolveTrainingText({ ...next, description: null }, next.translations, language);
  const length = next.durationSeconds && next.durationSeconds > 0 ? ` · ${formatTrainingDuration(next.durationSeconds)}` : '';
  return `Next: ${title}${length}`;
}

/** "7 of 12 lessons · Due in 3 days". */
export function continueMetaLine(entry: ContinueLearning, now: Date): string {
  const { lessonsCompleted, lessonsTotal, dueAt } = entry.assignment;
  const parts = [`${Math.min(lessonsCompleted, lessonsTotal)} of ${lessonsTotal} ${lessonsTotal === 1 ? 'lesson' : 'lessons'}`];
  const due = dueInWords(dueAt, now);
  if (due) parts.push(due);
  return parts.join(' · ');
}

const CONTINUE_LINK =
  'inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-md bg-accent px-4 text-sm font-semibold text-fg-on-accent transition-colors hover:bg-accent-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent';

/**
 * My learning's "Continue learning" hero (PLAN §4.1): the course touched most recently, the lesson
 * that opens next, how far through it the learner is, and one Continue button straight into the
 * player at that lesson.
 */
export function ContinueLearningHero({ entry, now }: { entry: ContinueLearning; now: Date }) {
  const { assignment } = entry;
  // In the language the learner last picked in the player (ADR 0039); the course's own otherwise.
  const { chosen: language } = useLessonLanguage();
  const task = localizeText(assignment.task, language);
  const percent = Number.parseFloat(assignment.progressPercent);
  const next = nextLessonLine(entry.nextLesson, language);
  const overdue = assignment.dueAt !== null && new Date(assignment.dueAt).getTime() < now.getTime();

  return (
    <section
      aria-labelledby="continue-learning-title"
      className="overflow-hidden rounded-2xl border border-border bg-surface shadow-sm"
    >
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:gap-5 sm:p-5">
        <CourseCover
          title={task.title}
          category={task.category}
          coverImageUrl={task.coverImageUrl}
          size="md"
          className="w-full shrink-0 sm:w-56 lg:w-64"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-accent">Continue learning</p>
          <h2 id="continue-learning-title" className="line-clamp-2 font-display text-lg font-bold leading-snug text-fg sm:text-xl">
            {task.title}
          </h2>
          {next && <p className="truncate text-sm text-fg-muted">{next}</p>}
          <div className="flex flex-col gap-1.5 pt-1">
            <LearningProgressBar
              percent={percent}
              label={`${task.title} progress`}
              className="max-w-md"
            />
            <p className={cn('text-xs tabular-nums', overdue ? 'font-semibold text-danger' : 'text-fg-muted')}>
              {continueMetaLine(entry, now)}
            </p>
          </div>
        </div>
        <Link
          to={{
            pathname: learnerCoursePath(assignment.id),
            search: entry.nextLesson ? `?lesson=${entry.nextLesson.id}` : '',
          }}
          className={cn(CONTINUE_LINK, 'self-stretch sm:self-center')}
        >
          <Play className="size-4 fill-current" aria-hidden="true" />
          Continue
        </Link>
      </div>
    </section>
  );
}
